import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BudgetDialog } from "./BudgetDialog";

interface Budget {
  id: string;
  category: string;
  amount: number;
  spent: number;
  period: string;
}

export const BudgetList = () => {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const { toast } = useToast();

  const fetchBudgets = async () => {
    try {
      const { data, error } = await supabase
        .from('budgets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBudgets(data || []);

    } catch (error) {
      console.error('Error fetching budgets:', error);
      toast({
        title: "Error",
        description: "Failed to load budgets. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Monthly Budgets</CardTitle>
        <BudgetDialog onBudgetCreated={fetchBudgets} />
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {budgets.map((budget) => (
            <div key={budget.id} className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">{budget.category}</span>
                <span className="text-gray-600">
                  ${budget.spent?.toFixed(2) || '0.00'} / ${budget.amount.toFixed(2)}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ 
                    width: `${((budget.spent || 0) / budget.amount) * 100}%`
                  }}
                />
              </div>
            </div>
          ))}
          {budgets.length === 0 && (
            <div className="text-center text-gray-500 py-4">
              No budgets created yet. Click "New Budget" to get started.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};